import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Clock, MessageCircle, Send, Loader2, Navigation, Mail } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { BRAND, WA_DEFAULT, IMAGES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-[#E2E8F0] bg-white px-5 py-3.5 text-sm text-[#1A2E2A] placeholder:text-[#94A3B8] outline-none focus:border-[#489A88] focus:ring-4 focus:ring-[#489A88]/10 transition-[border-color,box-shadow] duration-300";

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Messaggio inviato con successo!", {
        description: "Francesca ti ricontatterà al più presto. Grazie!",
      });
      setForm({ name: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Invio non riuscito", {
        description: "Riprova tra poco oppure scrivi direttamente su WhatsApp.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="bg-white pt-28 lg:pt-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          <Reveal>
            <Overline>06 — Contatti</Overline>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#1A2E2A] leading-[1.1]">
              Richiedi un <span className="italic text-[#489A88]">Appuntamento</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#4A5568] max-w-md">
              Compila il modulo e verrai ricontattato al più presto, oppure scrivimi direttamente
              su WhatsApp per una risposta immediata.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4" data-testid="contact-address">
                <span className="w-11 h-11 rounded-xl bg-[#E8F5F2] flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-[#2D6D60]" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-[#1A2E2A]">Studio</p>
                  <p className="text-sm text-[#64748B]">{BRAND.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-phone">
                <span className="w-11 h-11 rounded-xl bg-[#E8F5F2] flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-[#2D6D60]" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-[#1A2E2A]">Telefono e WhatsApp</p>
                  <a href={`tel:${BRAND.phoneRaw}`} className="text-sm text-[#64748B] hover:text-[#2D6D60] transition-colors">
                    {BRAND.phoneDisplay}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-email">
                <span className="w-11 h-11 rounded-xl bg-[#E8F5F2] flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-[#2D6D60]" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-[#1A2E2A]">Email</p>
                  <a href={`mailto:${BRAND.email}`} className="text-sm text-[#64748B] hover:text-[#2D6D60] transition-colors">
                    {BRAND.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4" data-testid="contact-hours">
                <span className="w-11 h-11 rounded-xl bg-[#E8F5F2] flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-[#2D6D60]" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-[#1A2E2A]">Orari di apertura</p>
                  {BRAND.hours.map((h) => (
                    <p key={h.days} className="text-sm text-[#64748B]">
                      {h.days}: <span className="text-[#1A2E2A] font-medium">{h.time}</span>
                    </p>
                  ))}
                </div>
              </div>
            </div>

            <a
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="mt-10 inline-flex items-center gap-2.5 rounded-full bg-[#25D366] text-white font-medium px-7 py-3.5 hover:bg-[#1DA851] transition-colors duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              Scrivimi su WhatsApp
            </a>
          </Reveal>

          <Reveal delay={0.15}>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="rounded-[2rem] bg-[#FAF8F5] border border-[#E2E8F0] p-8 sm:p-10 shadow-[0_20px_60px_-30px_rgba(26,46,42,0.25)]"
            >
              <div className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-2.5">
                    Nome e Cognome
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    required
                    placeholder="Il tuo nome"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-2.5">
                    Telefono
                  </label>
                  <input
                    id="contact-phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    required
                    placeholder="Il tuo numero di telefono"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-2.5">
                    Messaggio
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    placeholder="Raccontami di cosa hai bisogno: dolore, tensione, trattamento desiderato..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#1A2E2A] text-white font-medium px-8 py-4 hover:bg-[#489A88] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  {sending ? "Invio in corso..." : "Invia la richiesta"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="mt-20">
          <div
            data-testid="map-container"
            className="relative rounded-[2rem] overflow-hidden border border-[#E2E8F0] shadow-[0_20px_60px_-30px_rgba(26,46,42,0.3)]"
          >
            <iframe
              title="Mappa dello studio: Via Monte Carso 19, 20010 Vanzago (MI)"
              src="https://maps.google.com/maps?q=Via%20Monte%20Carso%2019%2C%2020010%20Vanzago%20MI&t=&z=15&ie=UTF8&iwloc=&output=embed"
              className="w-full h-[380px] border-0"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map-iframe"
            />
            <a
              href="https://www.google.com/maps/search/?api=1&query=Via+Monte+Carso+19%2C+20010+Vanzago+MI"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-open-button"
              className="absolute bottom-5 left-5 inline-flex items-center gap-2.5 rounded-full bg-white/90 backdrop-blur-md border border-white/60 px-5 py-3 text-sm font-medium text-[#1A2E2A] shadow-lg hover:bg-[#489A88] hover:text-white hover:border-[#489A88] transition-colors duration-300"
            >
              <Navigation className="w-4 h-4" />
              Indicazioni stradali
            </a>
          </div>
        </Reveal>
      </div>

      <footer data-testid="site-footer" className="bg-[#1A2E2A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-10">
            <div className="flex items-center gap-5">
              <img
                src={IMAGES.logo}
                alt="Logo Francesca Cicardi Massoterapista M.C.B."
                className="w-16 h-16 rounded-2xl object-cover"
                data-testid="footer-logo"
              />
              <div>
                <p className="font-['Outfit'] text-lg font-medium">Francesca Cicardi</p>
                <p className="text-sm text-white/60">Massoterapista M.C.B. · Vanzago (Milano)</p>
              </div>
            </div>
            <div className="text-sm text-white/60 space-y-1 md:text-right">
              <p>{BRAND.address}</p>
              <p>
                Tel. <a href={`tel:${BRAND.phoneRaw}`} className="text-white/85 hover:text-[#7BC4B2] transition-colors">{BRAND.phoneDisplay}</a>
                {" · "}
                <a href={`mailto:${BRAND.email}`} className="text-white/85 hover:text-[#7BC4B2] transition-colors">{BRAND.email}</a>
              </p>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4">
            <p className="text-xs text-white/45 max-w-xl leading-relaxed" data-testid="footer-legal">
              {BRAND.legal}
            </p>
            <p className="text-xs text-white/45">
              © {new Date().getFullYear()} Francesca Cicardi — Massoterapia. Tutti i diritti riservati. ·{" "}
              <Link to="/privacy" data-testid="footer-privacy-link" className="underline underline-offset-2 hover:text-[#7BC4B2] transition-colors">
                Privacy e Cookie Policy
              </Link>
            </p>
          </div>
        </div>
      </footer>
    </section>
  );
};

export default Contact;
